import { Injectable } from '@angular/core';
import { HashMap } from '@ngneat/transloco';
import {
  TokenizeResult,
  TranslationMarkupRenderer,
  TranslationMarkupRendererFactory,
  TranslationMarkupTranspilerContext,
  TranspileResult
} from 'ngx-transloco-markup';

export class LocalizationStart {
  constructor(readonly paramsKey: string) {}
}

export const LOCALIZATION_END = new (class LocalizationEnd {})();

@Injectable({
  providedIn: 'root'
})
export class LocalizationTranspilerService {
  tokenize(
    translation: string,
    offset: number,
    markupTag: string,
    createLocalizationStart: (paramsKey: string) => LocalizationStart
  ): TokenizeResult | undefined {
    return (
      this.recognizeLocalizationStartToken(translation, offset, markupTag, createLocalizationStart) || this.recognizeLocalizationEndToken(translation, offset)
    );
  }

  private recognizeLocalizationStartToken(
    translation: string,
    offset: number,
    markupTag: string,
    createLocalizationStart: (paramsKey: string) => LocalizationStart
  ): TokenizeResult | undefined {
    const LOCALIZATION_START_TOKEN = '[' + markupTag;

    if (!translation.startsWith(LOCALIZATION_START_TOKEN, offset)) {
      return undefined;
    }

    const afterStart = offset + LOCALIZATION_START_TOKEN.length;
    const end = translation.indexOf('/', afterStart);

    if (end < 0) {
      return undefined;
    }

    if (afterStart !== end && translation.charAt(afterStart) !== ':') {
      return undefined;
    }

    const paramsKey = afterStart !== end ? translation.substring(offset + LOCALIZATION_START_TOKEN.length + 1, end) : '';

    return {
      nextOffset: end + 1,
      token: createLocalizationStart(paramsKey)
    };
  }

  private recognizeLocalizationEndToken(translation: string, offset: number): TokenizeResult | undefined {
    const LOCALIZATION_END_TOKEN = ']';

    if (!translation.startsWith(LOCALIZATION_END_TOKEN, offset)) {
      return undefined;
    }

    return {
      nextOffset: offset + LOCALIZATION_END_TOKEN.length,
      token: LOCALIZATION_END
    };
  }

  transpile(
    start: number,
    context: TranslationMarkupTranspilerContext,
    createText: (translationParameters: HashMap, paramsKey: string) => string,
    rendererFactory: TranslationMarkupRendererFactory,
    getParamsKey: (token: unknown) => string | undefined
  ): TranspileResult | undefined {
    const nextToken = context.tokens[start];
    const paramsKey = getParamsKey(nextToken);

    if (!paramsKey) {
      return undefined;
    }

    const { nextOffset } = context.transpileUntil(start + 1, token => token === LOCALIZATION_END);

    return {
      nextOffset: Math.min(nextOffset + 1, context.tokens.length),
      renderer: this.createRenderer((translationParameters: HashMap) => createText(translationParameters, paramsKey), rendererFactory)
    };
  }

  private createRenderer(createText: (translationParameters: HashMap) => string, rendererFactory: TranslationMarkupRendererFactory): TranslationMarkupRenderer {
    const renderExternalLinkMarkup: (translationParameters: HashMap) => Node = translationParameters => {
      const enumerationText = rendererFactory.createTextRenderer(createText)(translationParameters);
      return enumerationText;
    };

    return renderExternalLinkMarkup;
  }
}
